import React, { useEffect, useState } from 'react';
import MeetingOrderForm from 'component/MeetingOrderForm';
import Breadcrumb from 'component/Breadcrumb';
import { getPageFrom, RedirectPage } from 'util/meeting';
import { useHistory } from 'react-router-dom';
import { RootState } from 'model';
import { useDispatch, useSelector } from 'react-redux';
import styles from './index.module.scss';
import { IMeetingOrderForm, IMeetingOrderFn } from 'type/meeting';
import { orderMeeting, resetConflictSchedules, resetMeetingOrderParams } from 'model/meeting';
import { IMeetingDetail } from 'type/meetingDetail';
import container from 'lib/container';
import qs from 'qs';
import { formatBookTime } from 'util/format';
import { completeMeetingOrderPointHubSuccess } from 'lib/pointHub';
import { message } from 'antd';
import { Meeting as MeetingApi } from 'api';
import type { V2MeetingRecommendForm } from 'api/recommend';
import { REBOOK_MEETING_NO_QUERY } from 'util/meetingExtendRebook';
import { getStaffId } from 'data/storage';

const SCHEDULE_SELECTED_DATE_RECOMMEND_QUERY = 'scheduleSelectedDateRecommend';

const getQueryStringValue = (value: unknown): string => {
  if (Array.isArray(value)) return getQueryStringValue(value[0]);
  return typeof value === 'string' ? value : '';
};

const buildScheduleSelectedDateRecommendParams = (value: Partial<IMeetingOrderForm>): Partial<V2MeetingRecommendForm> | undefined => {
  const timeValue = value as Partial<IMeetingOrderForm> & { beginTime?: number | string | null };
  const beginTime = Number(timeValue.beginTime || timeValue.startTime);
  const endTime = Number(timeValue.endTime);

  if (!Number.isFinite(beginTime) || !Number.isFinite(endTime) || !beginTime || !endTime) {
    return undefined;
  }

  return {
    beginTime,
    endTime
  };
};

const detailMemberToForm = (member: any): IMeetingOrderForm['members'][number] => ({
  id: member._id || member.id || member.staffId || '',
  refId: member.staffId || '',
  userId: member._id || member.id || '',
  memberName: member.realName || member.displayName || '',
  teamId: '',
  teamName: member.department || '',
  teamQualifiedName: member.department || '',
  memberNamePinyin: '',
  memberNameShortPinyin: '',
  mobile: member.mobile,
  jobTitle: member.jobTitle,
  cardPhoto: member.avatar || ''
});

const buildRebookMeetingFormValue = (meetingDetail: IMeetingDetail): Partial<IMeetingOrderForm> => {
  const detail = meetingDetail as any;
  const currentStaffId = getStaffId();
  const meetingAllocationViews = detail?.meetingAllocationViews || {};
  const members = (meetingDetail.members || [])
    .map((member) => member.user)
    .filter((member) => member && member.staffId !== currentStaffId)
    .map(detailMemberToForm);

  return {
    subject: detail.subject || '',
    privateSubject: detail.isPublic === 0,
    orderType: 'normal',
    rooms: [],
    address: '',
    members,
    agenda: detail.content || '',
    scope: detail.scope,
    remind: detail.noticeLabels || [],
    files: detail.meetingFolderViews || [],
    onlineMeetings: meetingAllocationViews.meetingTypes || [],
    seetings: {
      password: meetingAllocationViews.password || '',
      autoMute: meetingAllocationViews.muteNewParticipant || false,
      autoRecord: meetingAllocationViews.recording || false
    },
    reason: detail.reason || detail.meetingAuditRecordViewList?.[0]?.reason || '',
    overrunReason: '',
    serviceItems: detail.serviceItems || [],
    meetingServiceRequirements: detail.meetingServiceRequirements || ''
  };
};

const buildRebookRecommendForm = (meetingDetail: IMeetingDetail): V2MeetingRecommendForm => {
  const currentStaffId = getStaffId();
  const staffIdList = (meetingDetail.members || [])
    .map((member) => member.user?.staffId)
    .filter((staffId): staffId is string => Boolean(staffId && staffId !== currentStaffId));

  return {
    members: {
      staffIdList
    },
    beginTime: null,
    endTime: null,
    address: '',
    roomBookingForms: []
  };
};

const getRebookMeetingInitValue = async (meetingNo: string) => {
  const detailRes = await MeetingApi.getMeetingDetailsV2(meetingNo).toPromise();
  if (!detailRes?.data) throw new Error('empty meeting detail');

  return {
    formValue: buildRebookMeetingFormValue(detailRes.data),
    initialRecommendParams: buildRebookRecommendForm(detailRes.data)
  };
};

const OrderMeeting: React.FC = () => {
  const history = useHistory();
  const query = qs.parse(location.search.slice(1));
  const rebookMeetingNo = getQueryStringValue(query?.[REBOOK_MEETING_NO_QUERY]);
  const isScheduleSelectedDateRecommend = getQueryStringValue(query?.[SCHEDULE_SELECTED_DATE_RECOMMEND_QUERY]) === '1';

  const dispatch = useDispatch();
  const [crumbItems, setCrumbItems] = useState<{ path?: string; label: string }[]>([]);
  const [formValue, setFormValue] = useState<Partial<IMeetingOrderForm>>();
  const [initialRecommendParams, setInitialRecommendParams] = useState<Partial<V2MeetingRecommendForm>>();
  const meetingOrderParams = useSelector((state: RootState) => state.meeting.meetingOrderParams);

  const onConfirm: IMeetingOrderFn = (meeting) => {
    return new Promise((resolve) => {
      dispatch(
        orderMeeting({
          meeting,
          onSuccess: (meeting: IMeetingDetail) => {
            completeMeetingOrderPointHubSuccess(meeting, resolve, () => {
              container.sdk?.createMeetingSuccess(meeting.meetingNo);
              history.replace(`/details/${meeting.meetingNo}?from=${RedirectPage.SCHEDULE}`);
            });
          },
          onFail: (err: any) => {
            if (err?.response?.data?.code === 400403002) {
              const data = err?.response?.data?.data;
              resolve({ success: false, data: data });
            } else if (err?.response?.data?.code === 400406011) {
              resolve({ success: false, errMesaage: '预约人（部门）、会议主题、会议地点可能包含涉黄/涉暴/政治/敏感信息，请检查后重试' });
            } else {
              resolve({ success: false, data: null });
            }
          },
          redirect: false
        })
      );
    });
  };

  const onCancel = () => {
    // 对接迈聆 - 取消预约后执行
    container.sdk?.cancelCreateMeeting();

    history.goBack();
  };

  const getAndResetMeetingParams = (setNextFormValue: (value: Partial<IMeetingOrderForm>, recommendParams?: Partial<V2MeetingRecommendForm>) => void) => {
    if (rebookMeetingNo) {
      dispatch(resetMeetingOrderParams());
      getRebookMeetingInitValue(rebookMeetingNo)
        .then(({ formValue, initialRecommendParams }) => {
          setNextFormValue(formValue, initialRecommendParams);
        })
        .catch((err) => {
          console.warn('获取再次预约会议信息失败', err);
          message.error('获取会议信息失败');
        });
      return;
    }

    if (meetingOrderParams) {
      setNextFormValue(
        meetingOrderParams,
        isScheduleSelectedDateRecommend ? buildScheduleSelectedDateRecommendParams(meetingOrderParams) : undefined
      );
      // 清除会议参数
      dispatch(resetMeetingOrderParams());
    } else if (query?.startTime || query?.endTime || query?.subject) {
      const bookTime = formatBookTime(query.startTime as string, query.endTime as string);
      const queryRecommendParams = isScheduleSelectedDateRecommend
        ? buildScheduleSelectedDateRecommendParams({
          startTime: getQueryStringValue(query.startTime),
          endTime: getQueryStringValue(query.endTime)
        } as Partial<IMeetingOrderForm>)
        : undefined;
      setNextFormValue(
        { ...bookTime, subject: (query.subject as string) || '' },
        queryRecommendParams || (isScheduleSelectedDateRecommend ? buildScheduleSelectedDateRecommendParams(bookTime) : undefined)
      );
    }
  };

  useEffect(() => {
    let isMounted = true;
    setCrumbItems([getPageFrom(), { label: '会议预约' }]);
    getAndResetMeetingParams((value, recommendParams) => {
      if (!isMounted) return;
      setInitialRecommendParams(recommendParams);
      setFormValue(value);
    });
    return () => {
      isMounted = false;
      dispatch(resetConflictSchedules());
    };
  }, []);

  const aiRecommendEnabled = !rebookMeetingNo || Boolean(formValue && initialRecommendParams);
  const scheduleSelectedDateRecommendRequestKey =
    isScheduleSelectedDateRecommend && initialRecommendParams?.beginTime
      ? `${SCHEDULE_SELECTED_DATE_RECOMMEND_QUERY}:${initialRecommendParams.beginTime}`
      : '';

  return (
    <>
      <Breadcrumb items={crumbItems} />
      <div className={styles.body}>
        <MeetingOrderForm
          title="预约会议"
          formType="order"
          formValue={formValue}
          aiRecommendEnabled={aiRecommendEnabled}
          initialRecommendParams={initialRecommendParams}
          initialRecommendRequestKey={rebookMeetingNo || scheduleSelectedDateRecommendRequestKey}
          onConfirm={onConfirm}
          onCancel={onCancel}
          confirmText="发出预约"
        />
      </div>
    </>
  );
};

export default OrderMeeting;
